import { useEffect } from 'react';
import { POSE_CONNECTIONS } from '@/types/mediapipe';
import { hexToRgba } from '@/utils/colors';
import { cn } from '@/utils/cn';
import { setupCanvas } from '@/utils/canvasHelpers';
import {
  GHOST_ECHO,
  GHOST_OUTLINE,
  RENDERING,
  COMMON_COLORS,
  isLandmarkVisible,
} from '@/utils/visualizationConstants';

type Landmark = { x: number; y: number; visibility?: number };

/**
 * Translates and scales ghost landmarks so the ghost hip centroid and torso
 * scale match the athlete's, making the overlay position-independent of where
 * the reference video was recorded.
 *
 * Returns the original array unchanged if required landmarks are missing.
 */
function normalizeGhostToAthlete(
  ghostLandmarks: Landmark[],
  athleteLandmarks: Landmark[],
): Landmark[] {
  const [lHipIdx, rHipIdx] = GHOST_OUTLINE.HIP_INDICES;
  const [lShoulderIdx, rShoulderIdx] = GHOST_OUTLINE.SHOULDER_INDICES;

  const gLH = ghostLandmarks[lHipIdx];
  const gRH = ghostLandmarks[rHipIdx];
  const gLS = ghostLandmarks[lShoulderIdx];
  const gRS = ghostLandmarks[rShoulderIdx];

  const aLH = athleteLandmarks[lHipIdx];
  const aRH = athleteLandmarks[rHipIdx];
  const aLS = athleteLandmarks[lShoulderIdx];
  const aRS = athleteLandmarks[rShoulderIdx];

  // Guard: all eight anchor landmarks must be present
  if (!gLH || !gRH || !gLS || !gRS || !aLH || !aRH || !aLS || !aRS) {
    return ghostLandmarks;
  }

  // Ghost hip centroid
  const ghostCx = (gLH.x + gRH.x) / 2;
  const ghostCy = (gLH.y + gRH.y) / 2;

  // Athlete hip centroid
  const athleteCx = (aLH.x + aRH.x) / 2;
  const athleteCy = (aLH.y + aRH.y) / 2;

  // Torso scale = average of left and right shoulder-to-hip distances
  const ghostScale =
    (Math.hypot(gLS.x - gLH.x, gLS.y - gLH.y) +
      Math.hypot(gRS.x - gRH.x, gRS.y - gRH.y)) /
    2;
  const athleteScale =
    (Math.hypot(aLS.x - aLH.x, aLS.y - aLH.y) +
      Math.hypot(aRS.x - aRH.x, aRS.y - aRH.y)) /
    2;

  // Avoid division by zero
  if (ghostScale === 0) return ghostLandmarks;

  const scaleRatio = athleteScale / ghostScale;

  return ghostLandmarks.map((lm) => {
    if (!lm) return lm;
    // Translate to ghost centroid origin, scale, then translate to athlete centroid
    const nx = (lm.x - ghostCx) * scaleRatio + athleteCx;
    const ny = (lm.y - ghostCy) * scaleRatio + athleteCy;
    return { ...lm, x: nx, y: ny };
  });
}

interface GhostEchoOverlayProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  templateLandmarks: Array<{ x: number; y: number; visibility?: number }>; // 33 landmarks, normalized 0-1
  athleteLandmarks?: Array<{ x: number; y: number; visibility?: number }>; // Optional athlete skeleton
  /** Width of the actual video render area (may be smaller than container for pillarboxed video) */
  width: number;
  /** Height of the actual video render area (may be smaller than container for letterboxed video) */
  height: number;
  /** X offset from canvas left edge to the start of the video render area (pillarbox offset) */
  offsetX?: number;
  /** Y offset from canvas top edge to the start of the video render area (letterbox offset) */
  offsetY?: number;
  /** Full container width — canvas is sized to this so it covers the whole container */
  containerWidth?: number;
  /** Full container height — canvas is sized to this so it covers the whole container */
  containerHeight?: number;
  intensity: number; // 0-100, controls ghost opacity
  ghostColor?: string; // Default: teal from GHOST_ECHO.COLOR
  athleteColor?: string; // Default: white from COMMON_COLORS.WHITE
  showConnections?: boolean; // Draw skeleton lines
  className?: string;
}

function GhostEchoOverlay({
  canvasRef,
  templateLandmarks,
  athleteLandmarks,
  width,
  height,
  offsetX = 0,
  offsetY = 0,
  containerWidth,
  containerHeight,
  intensity,
  ghostColor = GHOST_ECHO.COLOR,
  athleteColor = COMMON_COLORS.WHITE,
  showConnections = true,
  className,
}: GhostEchoOverlayProps) {
  // Canvas covers the full container; coordinates are offset to the video render area.
  const canvasW = containerWidth ?? width;
  const canvasH = containerHeight ?? height;

  useEffect(() => {
    const ctx = setupCanvas(canvasRef, canvasW, canvasH);
    if (!ctx) return;

    try {
      // Skip rendering if intensity is too low (nearly invisible)
      if (intensity < RENDERING.MIN_INTENSITY) return;

      // Draw template skeleton (ghost) — dashed outline style, centroid-normalized
      if (templateLandmarks && templateLandmarks.length > 0) {
        const ghostOpacity = intensity / 100;
        const ghostColorWithAlpha = hexToRgba(ghostColor, ghostOpacity);

        // Align ghost position and scale to athlete body if athlete landmarks exist
        const normalizedGhost =
          athleteLandmarks && athleteLandmarks.length > 0
            ? normalizeGhostToAthlete(templateLandmarks, athleteLandmarks)
            : templateLandmarks;

        // Apply stronger glow for visual distinction
        ctx.shadowColor = ghostColor;
        ctx.shadowBlur = GHOST_OUTLINE.GLOW_BLUR;

        // Draw dashed connections — thicker than athlete lines
        if (showConnections) {
          ctx.setLineDash(GHOST_OUTLINE.DASH_PATTERN);
          ctx.lineWidth = GHOST_OUTLINE.LINE_WIDTH;

          POSE_CONNECTIONS.forEach(([startIdx, endIdx]) => {
            const start = normalizedGhost[startIdx];
            const end = normalizedGhost[endIdx];

            if (!start || !end) return;
            if (!isLandmarkVisible(start) || !isLandmarkVisible(end)) return;

            ctx.beginPath();
            ctx.moveTo(start.x * width + offsetX, start.y * height + offsetY);
            ctx.lineTo(end.x * width + offsetX, end.y * height + offsetY);
            ctx.strokeStyle = ghostColorWithAlpha;
            ctx.stroke();
          });

          // Reset dash so athlete lines are solid
          ctx.setLineDash([]);
        }

        // Draw hollow landmark rings (stroke only, no fill)
        normalizedGhost.forEach((landmark) => {
          if (!isLandmarkVisible(landmark)) return;

          ctx.beginPath();
          ctx.arc(
            landmark.x * width + offsetX,
            landmark.y * height + offsetY,
            GHOST_OUTLINE.LANDMARK_RADIUS,
            0,
            2 * Math.PI,
          );
          ctx.strokeStyle = ghostColorWithAlpha;
          ctx.lineWidth = GHOST_OUTLINE.LINE_WIDTH;
          ctx.stroke();
        });

        // Reset shadow before drawing athlete
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      }

      // Draw athlete skeleton (solid) on top — unchanged rendering
      if (athleteLandmarks && athleteLandmarks.length > 0) {
        const athleteColorRgba = hexToRgba(athleteColor, 1);

        // Draw connections
        if (showConnections) {
          POSE_CONNECTIONS.forEach(([startIdx, endIdx]) => {
            const start = athleteLandmarks[startIdx];
            const end = athleteLandmarks[endIdx];

            if (!start || !end) return;

            // Skip if visibility is too low
            if (!isLandmarkVisible(start) || !isLandmarkVisible(end)) return;

            ctx.beginPath();
            ctx.moveTo(start.x * width + offsetX, start.y * height + offsetY);
            ctx.lineTo(end.x * width + offsetX, end.y * height + offsetY);
            ctx.strokeStyle = athleteColorRgba;
            ctx.lineWidth = GHOST_ECHO.CONNECTION_WIDTH;
            ctx.stroke();
          });
        }

        // Draw landmarks
        athleteLandmarks.forEach((landmark) => {
          if (!isLandmarkVisible(landmark)) return;

          ctx.beginPath();
          ctx.arc(landmark.x * width + offsetX, landmark.y * height + offsetY, GHOST_ECHO.LANDMARK_RADIUS, 0, 2 * Math.PI);
          ctx.fillStyle = athleteColorRgba;
          ctx.fill();
        });
      }
    } catch (error) {
      console.error('[GhostEchoOverlay] Canvas drawing error:', error);
    }
  }, [
    canvasRef,
    templateLandmarks,
    athleteLandmarks,
    width,
    height,
    offsetX,
    offsetY,
    canvasW,
    canvasH,
    intensity,
    ghostColor,
    athleteColor,
    showConnections,
  ]);

  return (
    <canvas
      ref={canvasRef}
      width={canvasW}
      height={canvasH}
      className={cn('absolute inset-0 pointer-events-none', className)}
      style={{ width: '100%', height: '100%' }}
      aria-label="Ghost echo overlay"
    />
  );
}

export default GhostEchoOverlay;
